import { cn } from '@/lib/utils';
import React from 'react';

export type InlineToastVariant = 'default' | 'error';
export type InlineToastPosition = 'fixed' | 'absolute';

export type InlineToastProps = {
  /** When false, nothing is rendered. */
  show: boolean;
  /** Color treatment. `default` = dark pill, `error` = red pill. */
  variant?: InlineToastVariant;
  /** Positioning context. `fixed` = viewport, `absolute` = nearest positioned ancestor. */
  position?: InlineToastPosition;
  /** Optional leading icon. When provided, renders with an icon-bubble layout. */
  icon?: React.ReactNode;
  /** Tailwind classes for the icon bubble background. Only used when `icon` is set. */
  iconBubbleClassName?: string;
  /** Extra classes appended to the root. */
  className?: string;
  children: React.ReactNode;
};

/**
 * Lightweight bottom-center inline feedback (e.g. "Link copied"). Lives in
 * the same DOM tree as the action, sliding up briefly. For API-level
 * success/failure notifications, use the global Toast system instead.
 */
export const InlineToast: React.FC<InlineToastProps> = ({
  show,
  variant = 'default',
  position = 'fixed',
  icon,
  iconBubbleClassName,
  className,
  children,
}) => {
  if (!show) return null;

  const variantClasses =
    variant === 'error'
      ? 'bg-red-600 text-white'
      : 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900';

  const layoutClasses = icon
    ? 'flex items-center gap-3 px-3 py-2 rounded-xl'
    : 'px-4 py-2 rounded-lg';

  return (
    <div
      className={cn(
        position,
        'bottom-4 left-1/2 -translate-x-1/2 z-50 shadow-lg',
        'animate-in fade-in slide-in-from-bottom-2 duration-200',
        'text-sm font-medium',
        variantClasses,
        layoutClasses,
        className
      )}
    >
      {icon && (
        <div
          className={cn(
            'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center',
            iconBubbleClassName ?? 'bg-white/15'
          )}
        >
          {icon}
        </div>
      )}
      {children}
    </div>
  );
};

InlineToast.displayName = 'InlineToast';
